<template>
  <Modal
    :model-value="true"
    show-close
    :content-class="showCreateForm ? 'max-w-5xl' : 'max-w-2xl'"
    @update:model-value="handleOpenChange"
  >
    <template #title>
      <div class="flex w-full items-center justify-between gap-2">
        <span class="min-w-0 truncate">
          {{
            showCreateForm
              ? t('createUserTitle')
              : lead
                ? `${lead.firstName} ${lead.lastName}`
                : t('loading')
          }}
        </span>
        <AuditLogButton
          v-if="lead && !showCreateForm"
          :entity-type="AuditLogEntityType.LEAD"
          :entity-id="lead.id"
        />
      </div>
    </template>

    <div v-if="!lead" class="flex justify-center py-12">
      <Spinner class="text-hazel-600" />
    </div>

    <div v-else-if="showCreateForm" class="space-y-4">
      <CVButton variant="link" @click="showCreateForm = false">
        <ArrowLeftIcon class="size-5" />
        {{ t('backToLead') }}
      </CVButton>
      <div v-if="me && stations" class="text-sm">
        <UserForm
          :me="me"
          :stations="stations"
          :is-loading="isSavingUser"
          :is-fetching="false"
          :lead-prefill="leadPrefill"
          @on-submit="handleCreateSubmit"
        />
      </div>
      <div v-else class="flex justify-center py-12">
        <Spinner class="text-hazel-600" />
      </div>
    </div>

    <div v-else class="space-y-4 text-sm">
      <p class="rounded bg-hazel-50 p-3 text-obsidian-700">
        {{ t('processNote') }}
      </p>

      <StatusBanner v-if="existingUser && !isCompleted" variant="warning">
        {{ t('emailExistsText', { email: lead.email }) }}
        <template #actions>
          <CVButton
            variant="secondary"
            size="sm"
            :to="{ name: 'user', params: { id: existingUser.id } }"
          >
            {{ t('openUser') }}
          </CVButton>
          <CVButton size="sm" :is-loading="isLinking" @click="handleLinkExistingUser">
            {{ t('linkUser') }}
          </CVButton>
        </template>
      </StatusBanner>

      <StatusBanner v-if="hasDuplicateLeadEmail" variant="warning">
        {{ t('duplicateLeadEmailText', { email: lead.email }) }}
      </StatusBanner>

      <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="min-w-0">
          <dt class="text-obsidian-500">{{ t('email') }}</dt>
          <dd class="flex min-w-0 items-center gap-1">
            <EmailLink :email="lead.email" class="min-w-0" />
            <CopyToClipboardButton :text="lead.email" class="shrink-0 text-obsidian-500" />
          </dd>
        </div>
        <div class="min-w-0">
          <dt class="text-obsidian-500">{{ t('phone') }}</dt>
          <dd class="flex min-w-0 items-center gap-1">
            <PhoneLink :phone-number="lead.phone" class="min-w-0" />
            <CopyToClipboardButton :text="lead.phone" class="shrink-0 text-obsidian-500" />
          </dd>
        </div>
        <div class="min-w-0">
          <dt class="text-obsidian-500">{{ t('company') }}</dt>
          <dd class="break-words">{{ lead.company }}</dd>
        </div>
        <div>
          <dt class="text-obsidian-500">{{ t('plannedBookings') }}</dt>
          <dd>{{ lead.plannedBookingsPerMonth }}</dd>
        </div>
        <div v-if="lead.outcome" class="sm:col-span-2">
          <dt class="text-obsidian-500">{{ t('outcome') }}</dt>
          <dd>{{ outcomeLabel }}</dd>
        </div>
        <div v-if="lead.user" class="sm:col-span-2">
          <dt class="text-obsidian-500">{{ t('linkedUser') }}</dt>
          <dd>
            <RouterLink
              class="text-bittersweet-600 underline"
              :to="{ name: 'user', params: { id: lead.user.id } }"
            >
              {{ lead.user.firstName }} {{ lead.user.lastName }}
            </RouterLink>
          </dd>
        </div>
        <div v-else-if="lead.approvalRequest" class="sm:col-span-2">
          <dt class="text-obsidian-500">{{ t('approval') }}</dt>
          <dd>{{ t('approvalPending', { status: lead.approvalRequest.status }) }}</dd>
        </div>
      </dl>

      <CommentsPanel entity-type="Lead" :entity-id="lead.id" :entity="lead" />

      <div
        v-if="!isCompleted"
        class="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end"
      >
        <CVButton
          v-if="lead.status === LeadStatus.NEW"
          :is-loading="isMarkingContacted"
          @click="handleContacted"
        >
          {{ t('markContacted') }}
        </CVButton>
        <CVButton
          v-if="hasCheckedExistingUser && !existingUser"
          variant="secondary"
          @click="showCreateForm = true"
        >
          {{ t('createUser') }}
        </CVButton>
        <CVButton variant="danger" :is-loading="isDismissing" @click="handleDismiss">
          {{ t('dismiss') }}
        </CVButton>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { AuditLogEntityType, LeadOutcome, LeadStatus, UserRole } from '@carvia/ros-client-types';
import type { CreateUserDto, UpdateUserDto } from '@/entities/auth/user.entity';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import PhoneLink from '@/components/PhoneLink.vue';
import EmailLink from '@/components/EmailLink.vue';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import AuditLogButton from '@/components/audit-log/AuditLogButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import CommentsPanel from '@/components/comments/CommentsPanel.vue';
import UserForm from '@/views/users/user/UserForm.vue';
import { ArrowLeftIcon } from 'lucide-vue-next';
import { useLead, useUpdateLead, useLeadsByEmail } from '@/queries/use-leads';
import { useCreateUser, useMe, useUserWithEmailIfExists } from '@/queries/use-users';
import { useStations } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';

const props = defineProps<{
  leadId: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const { data: lead, isError } = useLead(() => props.leadId);

watch(isError, (error) => {
  if (error) emit('close');
});

const showCreateForm = ref(false);
watch(
  () => props.leadId,
  () => {
    showCreateForm.value = false;
  },
);

const isCompleted = computed(() => lead.value?.status === LeadStatus.COMPLETED);
const outcomeLabel = computed(() => {
  if (lead.value?.outcome === LeadOutcome.CONVERTED) return t('outcomeConverted');
  if (lead.value?.outcome === LeadOutcome.DISMISSED) return t('outcomeDismissed');
  return '';
});

const leadPrefill = computed(() => {
  if (!lead.value) return undefined;
  return {
    leadId: lead.value.id,
    firstName: lead.value.firstName,
    lastName: lead.value.lastName,
    email: lead.value.email,
    phone: lead.value.phone,
    company: lead.value.company,
    role: UserRole.AGENCY,
  };
});

const { data: existingUser, isSuccess: hasCheckedExistingUser } = useUserWithEmailIfExists(
  () => lead.value?.email ?? '',
  () => !!lead.value && !isCompleted.value && !showCreateForm.value,
);

const { data: leadsWithSameEmail } = useLeadsByEmail(
  () => lead.value?.email ?? '',
  () => !!lead.value && !showCreateForm.value,
);
const hasDuplicateLeadEmail = computed(
  () =>
    !!lead.value &&
    (leadsWithSameEmail.value?.items ?? []).some((item) => item.id !== lead.value!.id),
);

const { data: me } = useMe();
const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const { mutateAsync: updateLead, isPending, variables } = useUpdateLead();
const { mutateAsync: createUser, isPending: isSavingUser } = useCreateUser();

const isMarkingContacted = computed(
  () =>
    isPending.value &&
    variables.value?.data.status === LeadStatus.CONTACTED &&
    !variables.value.data.outcome,
);
const isDismissing = computed(
  () => isPending.value && variables.value?.data.outcome === LeadOutcome.DISMISSED,
);
const isLinking = computed(() => isPending.value && !!variables.value?.data.userId);

const handleOpenChange = (open: boolean) => {
  if (!open) emit('close');
};

const handleContacted = async () => {
  if (!lead.value) return;
  await updateLead({
    id: lead.value.id,
    data: { status: LeadStatus.CONTACTED },
    successMessage: 'Lead contacted',
  });
  emit('close');
};

const handleDismiss = async () => {
  if (!lead.value) return;
  await updateLead({
    id: lead.value.id,
    data: { status: LeadStatus.COMPLETED, outcome: LeadOutcome.DISMISSED },
    successMessage: 'Lead closed',
  });
  emit('close');
};

const handleLinkExistingUser = async () => {
  if (!existingUser.value || !lead.value) return;
  await updateLead({
    id: lead.value.id,
    data: {
      status: LeadStatus.COMPLETED,
      outcome: LeadOutcome.CONVERTED,
      userId: existingUser.value.id,
    },
    successMessage: 'Lead linked',
  });
  emit('close');
};

const handleCreateSubmit = async (formValues: UpdateUserDto) => {
  if (!lead.value) return;
  await createUser({
    ...(formValues as CreateUserDto),
    leadId: lead.value.id,
  });
  emit('close');
};
</script>

<i18n lang="json">
{
  "en": {
    "loading": "Loading…",
    "processNote": "Please Google the customer first, then call. If unreachable, send an email right away and optionally use comments for documentation.",
    "email": "Email",
    "phone": "Phone",
    "company": "Company",
    "plannedBookings": "Planned bookings / month",
    "markContacted": "Customer was contacted",
    "createUser": "Create customer directly",
    "createUserTitle": "Create user",
    "backToLead": "Back to lead",
    "dismiss": "Close lead",
    "outcome": "Outcome",
    "outcomeConverted": "Converted",
    "outcomeDismissed": "Dismissed",
    "linkedUser": "Linked user",
    "approval": "Approval",
    "approvalPending": "Pending approval ({status})",
    "emailExistsText": "A user with email {email} already exists.",
    "duplicateLeadEmailText": "Another lead with email {email} already exists.",
    "openUser": "Open user",
    "linkUser": "Link to lead"
  },
  "de": {
    "loading": "Laden…",
    "processNote": "Bitte Kunden erst googeln, dann anrufen. Wenn nicht erreicht wird, direkt eine E-Mail schreiben und optional Kommentare zur Dokumentation nutzen.",
    "email": "E-Mail",
    "phone": "Telefon",
    "company": "Firma",
    "plannedBookings": "Geplante Buchungen / Monat",
    "markContacted": "Kunde wurde kontaktiert",
    "createUser": "Kunde direkt anlegen",
    "createUserTitle": "Benutzer erstellen",
    "backToLead": "Zurück zum Lead",
    "dismiss": "Lead schließen",
    "outcome": "Ergebnis",
    "outcomeConverted": "Konvertiert",
    "outcomeDismissed": "Geschlossen",
    "linkedUser": "Verknüpfter User",
    "approval": "Freigabe",
    "approvalPending": "Freigabe ausstehend ({status})",
    "emailExistsText": "Ein User mit der E-Mail {email} existiert bereits.",
    "duplicateLeadEmailText": "Es existiert bereits ein weiterer Lead mit der E-Mail {email}.",
    "openUser": "User öffnen",
    "linkUser": "Mit Lead verknüpfen"
  },
  "es": {
    "loading": "Cargando…",
    "processNote": "Busca primero al cliente en Google y luego llama. Si no responde, envía un email y usa comentarios si hace falta.",
    "email": "Email",
    "phone": "Teléfono",
    "company": "Empresa",
    "plannedBookings": "Reservas previstas / mes",
    "markContacted": "Cliente contactado",
    "createUser": "Crear cliente directamente",
    "createUserTitle": "Crear usuario",
    "backToLead": "Volver al lead",
    "dismiss": "Cerrar lead",
    "outcome": "Resultado",
    "outcomeConverted": "Convertido",
    "outcomeDismissed": "Descartado",
    "linkedUser": "Usuario vinculado",
    "approval": "Aprobación",
    "approvalPending": "Aprobación pendiente ({status})",
    "emailExistsText": "Ya existe un usuario con el email {email}.",
    "duplicateLeadEmailText": "Ya existe otro lead con el email {email}.",
    "openUser": "Abrir usuario",
    "linkUser": "Vincular al lead"
  }
}
</i18n>
